import { useCallback, useEffect, useRef, useState } from "react";
import "./inviteLanding.css";

const publicAssetUrl = (fileName) =>
  `${process.env.PUBLIC_URL}/assets/${fileName}`;

function announceCopyFeedback(setFeedback, setHasFeedback, feedbackTimerRef, message) {
  clearTimeout(feedbackTimerRef.current);
  setFeedback(message);
  setHasFeedback(Boolean(message));
  if (message === "Code copied!") {
    feedbackTimerRef.current = setTimeout(() => {
      setFeedback("");
      setHasFeedback(false);
    }, 5000);
  }
}

function legacyCopy(value, copyButtonRef) {
  const field = document.createElement("textarea");
  field.value = value;
  field.readOnly = true;
  field.style.cssText =
    "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px";
  document.body.appendChild(field);
  field.select();
  field.setSelectionRange(0, value.length);
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    /* Manual selection follows. */
  }
  field.remove();
  copyButtonRef.current?.focus({ preventScroll: true });
  return copied;
}

function selectGiftCode(giftCodeRef) {
  const codeElement = giftCodeRef.current;
  if (!codeElement) return;
  codeElement.focus({ preventScroll: true });
  try {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(codeElement);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    /* The displayed code remains available for long-press selection. */
  }
}

export default function InviteCodeLanding({ inviterName, giftCode, appStoreUrl }) {
  const [copyFeedback, setCopyFeedback] = useState("");
  const [hasCopyFeedback, setHasCopyFeedback] = useState(false);
  const giftCodeRef = useRef(null);
  const copyButtonRef = useRef(null);
  const feedbackTimerRef = useRef(undefined);
  const renderRevisionRef = useRef(0);

  const trimmedInviterName =
    typeof inviterName === "string" ? inviterName.trim() : "";
  const trimmedGiftCode = typeof giftCode === "string" ? giftCode.trim() : "";
  const displayInviterName = trimmedInviterName || "A friend";
  const isLongCode =
    Array.from(trimmedGiftCode).length > 9 || trimmedGiftCode.length === 0;

  useEffect(() => {
    renderRevisionRef.current += 1;
    document.title = "A gift of Superfy Premium";
    if (!trimmedGiftCode) {
      announceCopyFeedback(
        setCopyFeedback,
        setHasCopyFeedback,
        feedbackTimerRef,
        "Your Super Code is unavailable. Please request a new invitation."
      );
    } else {
      announceCopyFeedback(setCopyFeedback, setHasCopyFeedback, feedbackTimerRef, "");
    }
    const feedbackTimer = feedbackTimerRef.current;
    return () => clearTimeout(feedbackTimer);
  }, [trimmedGiftCode, trimmedInviterName]);

  const handleCopyClick = useCallback(async () => {
    if (!trimmedGiftCode) return;
    const startedAt = renderRevisionRef.current;
    let copied = false;
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(trimmedGiftCode);
        copied = true;
      }
    } catch {
      /* Permission denied or clipboard unavailable: use the fallback. */
    }
    if (startedAt !== renderRevisionRef.current) return;
    if (!copied) copied = legacyCopy(trimmedGiftCode, copyButtonRef);
    if (copied) {
      announceCopyFeedback(
        setCopyFeedback,
        setHasCopyFeedback,
        feedbackTimerRef,
        "Code copied!"
      );
    } else {
      selectGiftCode(giftCodeRef);
      announceCopyFeedback(
        setCopyFeedback,
        setHasCopyFeedback,
        feedbackTimerRef,
        "Select and copy your code: press and hold it, or use Ctrl+C / ⌘C."
      );
    }
  }, [trimmedGiftCode]);

  return (
    <>
      <div className="artwork" aria-hidden="true">
        <img src={publicAssetUrl("rays.png")} alt="" />
      </div>
      <main className="invitation" aria-labelledby="gift-heading">
        <header className="gift">
          <div className="logo-slot">
            <img
              className="logo"
              src={publicAssetUrl("superfy-gold-face.png")}
              alt="Superfy"
              width="196"
              height="196"
            />
          </div>
          <h1 id="gift-heading">
            <span className="gift-intro">
              <bdi>{displayInviterName}</bdi> gifted you with
            </span>
            <span className="gift-title">1 month of Superfy Premium</span>
          </h1>
        </header>

        <h2 className="join-heading" id="join-heading">
          Join now!
        </h2>

        <section
          className={`steps-panel${hasCopyFeedback ? " has-feedback" : ""}`}
          aria-labelledby="join-heading"
        >
          <div className="step">
            <p className="step-label">
              <strong>Step 1</strong>: Download app to your device
            </p>
            <a
              className="app-store"
              href={appStoreUrl}
              aria-label="Download Superfy on the App Store"
            >
              <img
                className="badge-background"
                src={publicAssetUrl("app-store-background.svg")}
                alt=""
                aria-hidden="true"
              />
              <img
                className="badge-apple"
                src={publicAssetUrl("app-store-apple.svg")}
                alt=""
                aria-hidden="true"
              />
              <img
                className="badge-title"
                src={publicAssetUrl("app-store-title.svg")}
                alt=""
                aria-hidden="true"
              />
              <img
                className="badge-subtitle"
                src={publicAssetUrl("app-store-subtitle.svg")}
                alt=""
                aria-hidden="true"
              />
            </a>
          </div>
          <div className="step">
            <p className="step-label" id="code-label">
              <strong>Step 2</strong>: Enter the Super Code
            </p>
            <div className="code-field">
              <span
                className={`gift-code${isLongCode ? " is-long" : ""}`}
                id="gift-code"
                dir="ltr"
                tabIndex={-1}
                aria-labelledby="code-label"
                ref={giftCodeRef}
              >
                {trimmedGiftCode || "Unavailable"}
              </span>
              <button
                className="copy-button"
                type="button"
                aria-label="Copy Super Code"
                aria-describedby="copy-feedback"
                disabled={!trimmedGiftCode}
                ref={copyButtonRef}
                onClick={handleCopyClick}
              >
                <img src={publicAssetUrl("copy.svg")} alt="" aria-hidden="true" />
              </button>
            </div>
          </div>
          <p
            className="copy-feedback"
            id="copy-feedback"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {copyFeedback}
          </p>
        </section>
      </main>
    </>
  );
}
